/**
 * Fiches Pédagogiques — « L'Histoire en 2 minutes » (Le Two-Minute Drill de Peter Lynch)
 * 
 * "Avant d'acheter une action, vous devriez être capable d'expliquer à un enfant de 11 ans 
 * ce que fait l'entreprise, comment elle gagne de l'argent et ce qui fait monter son cours 
 * en moins de 2 minutes." — Peter Lynch (One Up On Wall Street, Chapitre 10)
 */

export interface CompanyStory {
  symbole: string;
  nomCourt: string;
  slogan: string;
  activite: string;
  modeleEco: string;
  matierePremiere: string;
  impactCorrelation: string;
  produitQuotidien: string;
  profilLynch: 'Fast Grower' | 'Stalwart' | 'Cyclique' | 'Rendement Dividende' | 'Turnaround' | 'Monopole / Rente';
  emoji: string;
}

export const COMPANY_STORIES: Record<string, CompanyStory> = {
  // --- AGRO-INDUSTRIE ---
  // NB: 'SAPH' est un symbole legacy mort (nom_societe NULL, is_active=false, cf. CLAUDE.md
  // "28 lignes parasites"). Le symbole réellement coté est SPHC — pas de fiche dupliquée ici.
  SPHC: {
    symbole: 'SPHC',
    nomCourt: 'SAPH CI',
    slogan: 'L’arbre à caoutchouc qui fait rouler le monde',
    activite: 'SAPH possède des milliers d’hectares d’arbres (l’hévéa) en Côte d’Ivoire. Chaque matin, la sève blanche (le latex) est récoltée dans des bols puis transformée en usine en gros blocs de caoutchouc naturel.',
    modeleEco: 'Elle vend ces blocs de caoutchouc brut par conteneurs entiers aux grands fabricants mondiaux de pneumatiques (Michelin, Goodyear, Continental).',
    matierePremiere: 'Caoutchouc Naturel (SICOM / TSR20)',
    impactCorrelation: 'Quand le cours mondial du caoutchouc monte à la Bourse de Singapour, les bénéfices de SAPH s’envolent automatiquement, même à production constante.',
    produitQuotidien: 'Les pneus de voiture, de camion et les semelles de baskets.',
    profilLynch: 'Cyclique',
    emoji: '🌳'
  },
  PALC: {
    symbole: 'PALC',
    nomCourt: 'Palmci CI',
    slogan: 'L’or rouge végétal de nos cuisines et savons',
    activite: 'Cultive des régimes de graines de palme et extrait l’huile de palme brute et l’huile de palmiste dans ses grandes huileries intégrées.',
    modeleEco: 'Vend l’huile de palme brute aux raffineries et savonneries locales (comme Sania / Dinor) qui fabriquent les huiles de friture, margarines et savons de ménage.',
    matierePremiere: 'Huile de Palme Brute (CPO)',
    impactCorrelation: 'Son résultat net dépend directement des cours mondiaux de l’huile de palme en Malaisie et en Indonésie, ainsi que de la météo des récoltes.',
    produitQuotidien: 'L’huile de cuisson Dinor, les savons ménagers et les biscuits.',
    profilLynch: 'Cyclique',
    emoji: '🌴'
  },
  SOGC: {
    symbole: 'SOGC',
    nomCourt: 'SOGB',
    slogan: 'Le double moteur : Caoutchouc naturel & Huile de palme',
    activite: 'Immense domaine agro-industriel au sud-ouest ivoirien (Grand-Béréby) produisant simultanément du caoutchouc naturel et de l’huile de palme.',
    modeleEco: 'Encaisse des devises à l’exportation pour le caoutchouc (Asie/Europe) et vend son huile de palme sur le marché régional ouest-africain.',
    matierePremiere: 'Caoutchouc (TSR20) & Huile de Palme (CPO)',
    impactCorrelation: 'Bénéficie d’une double exposition : si l’un des deux cours mondiaux monte, il compense l’autre et soutient des dividendes élevés.',
    produitQuotidien: 'Pneus automobiles et savons de toilette.',
    profilLynch: 'Cyclique',
    emoji: '🥥'
  },
  SHEC: {
    symbole: 'SHEC',
    nomCourt: 'Vivo Energy CI (Shell)',
    slogan: 'La marque coquille Shell qui fait le plein depuis 1927',
    activite: 'Vivo Energy exploite un réseau d’environ 170 stations-service sous la marque Shell en Côte d’Ivoire, ainsi que le ravitaillement des avions, des mines et des chantiers.',
    modeleEco: 'Prend une marge commerciale par litre de carburant, de lubrifiant (Shell Helix) et de gaz vendu, plus les ventes des boutiques Shell Select attenantes aux stations.',
    matierePremiere: 'Pétrole Brut (Brent) & Trafic Routier',
    impactCorrelation: 'Les volumes vendus suivent le nombre de véhicules en circulation et l’activité minière/industrielle ; les prix à la pompe restent encadrés par l’État.',
    produitQuotidien: 'Le plein d’essence à la station Shell et le bidon d’huile moteur Shell Helix.',
    profilLynch: 'Stalwart',
    emoji: '🐚'
  },
  SCRC: {
    symbole: 'SCRC',
    nomCourt: 'Sucrivoire',
    slogan: 'La canne à sucre qui adoucit le quotidien',
    activite: 'Filiale du groupe agro-industriel SIFCA, Sucrivoire cultive la canne à sucre sur environ 14 000 hectares irrigués et la transforme en sucre raffiné blanc et roux dans deux usines.',
    modeleEco: 'Vend des morceaux de sucre et des sacs de sucre industriel aux ménages, pâtissiers, brasseries et industriels agroalimentaires.',
    matierePremiere: 'Cours mondial du Sucre & Pluviométrie',
    impactCorrelation: 'Fortement influencée par la pluviométrie, les coûts de l’énergie (fours) et le cours mondial du sucre qui oriente les prix de référence.',
    produitQuotidien: 'Le sucre Princesse en morceaux dans le café ou le thé.',
    profilLynch: 'Cyclique',
    emoji: '🌾'
  },

  // --- TÉLÉCOMS & TECHNOLOGIE ---
  SNTS: {
    symbole: 'SNTS',
    nomCourt: 'Sonatel (Orange)',
    slogan: 'Le géant des télécoms et du mobile money ouest-africain',
    activite: 'Fournit la téléphonie mobile, l’internet très haut débit (fibre optique, 4G/5G) et les services financiers au Sénégal, Mali, Guinée, Bissau et Sierra Leone.',
    modeleEco: 'Gagne de l’argent chaque seconde quand quelqu’un recharge son forfait internet, regarde YouTube, passe un appel ou transfère de l’argent avec Orange Money.',
    matierePremiere: 'Consommation Data Mobile & Taux USD/EUR',
    impactCorrelation: 'Corrélation directe avec l’explosion de l’usage d’internet sur smartphone et le volume des paiements mobiles Orange Money.',
    produitQuotidien: 'La connexion 4G/5G, la fibre optique à la maison et l’application Orange Money.',
    profilLynch: 'Fast Grower',
    emoji: '📱'
  },
  ORAC: {
    symbole: 'ORAC',
    nomCourt: 'Orange CI',
    slogan: 'Le champion numérique et mobile de Côte d’Ivoire',
    activite: 'Premier opérateur de télécommunications en Côte d’Ivoire avec plus de 15 millions de clients, coté à la BRVM depuis décembre 2022 (plus grosse introduction de l’histoire du marché).',
    modeleEco: 'Encaisse des abonnements data récurrents, des forfaits d’entreprises et prélève une micro-commission sur chaque transaction Orange Money.',
    matierePremiere: 'Trafic Internet & Numérisation des paiements',
    impactCorrelation: 'Tire profit de la croissance démographique jeune, de la pénétration des smartphones et de l’essor du commerce digital.',
    produitQuotidien: 'Le pass internet 1 Go, la box fibre du salon et les retraits Orange Money.',
    profilLynch: 'Stalwart',
    emoji: '📶'
  },
  ORGT: {
    symbole: 'ORGT',
    nomCourt: 'Oragroup Togo',
    slogan: 'La banque panafricaine née à Lomé, présente dans 12 pays',
    activite: 'Oragroup est un groupe bancaire panafricain basé à Lomé, actif dans 12 pays d’Afrique de l’Ouest et Centrale (dont le Bénin, le Burkina Faso, la Côte d’Ivoire, le Sénégal, le Mali, le Niger, le Tchad et le Gabon).',
    modeleEco: 'Gagne la marge d’intérêt sur les prêts aux entreprises et aux particuliers de ses filiales, plus les commissions bancaires classiques (cartes, virements, opérations de change).',
    matierePremiere: 'Taux Directeurs (BCEAO/BEAC) & Qualité du Portefeuille de Crédit',
    impactCorrelation: 'Sensible à la conjoncture des 12 pays où le groupe opère et au coût du risque sur ses crédits aux PME.',
    produitQuotidien: 'Les agences Orabank que l’on croise dans plusieurs capitales ouest et centre-africaines.',
    profilLynch: 'Fast Grower',
    emoji: '🏦'
  },
  ONTBF: {
    symbole: 'ONTBF',
    nomCourt: 'Moov Africa BF (ex-Onatel)',
    slogan: 'L’opérateur télécom historique du Burkina Faso',
    activite: 'Assure la couverture mobile, internet fixe et le transfert d’argent (Moov Money) à travers tout le Burkina Faso ; filiale du groupe Maroc Telecom, rebaptisée Moov Africa Burkina Faso.',
    modeleEco: 'Vente de cartes de recharge, forfaits data prépayés et services aux entreprises.',
    matierePremiere: 'Pénétration smartphone & Stabilité réseau',
    impactCorrelation: 'Corrélation forte avec la consommation data des ménages burkinabè et les volumes échangés sur Moov Money.',
    produitQuotidien: 'La carte SIM Moov Africa et les recharges internet.',
    profilLynch: 'Rendement Dividende',
    emoji: '📡'
  },

  // --- ÉNERGIE & SERVICES PUBLICS ---
  TTLC: {
    symbole: 'TTLC',
    nomCourt: 'TotalEnergies CI',
    slogan: 'Le réseau d’énergie et de stations au cœur de la route',
    activite: 'Distribue des carburants (essence, gasoil), des lubrifiants, du gaz domestique et gère des boutiques de proximité dans plus de 180 stations-service.',
    modeleEco: 'Prend une marge commerciale fixée par litre distribué, plus les marges sur les huiles moteur, le gaz en bouteille et les ventes en boutique Bonjour.',
    matierePremiere: 'Pétrole Brut (Brent) & Trafic Routier',
    impactCorrelation: 'Le volume vendu dépend du dynamisme économique et du nombre de véhicules en circulation. Les prix sont encadrés par l’État.',
    produitQuotidien: 'Le plein d’essence à la station-service et la bouteille de gaz Total pour cuisiner.',
    profilLynch: 'Stalwart',
    emoji: '⛽'
  },
  TTLS: {
    symbole: 'TTLS',
    nomCourt: 'TotalEnergies Sénégal',
    slogan: 'L’énergie en mouvement sur les routes du Sénégal',
    activite: 'Réseau leader de stations-service, de distribution de carburant pour l’aviation et de lubrifiants au Sénégal.',
    modeleEco: 'Marge commerciale sur la distribution de produits pétroliers raffinés et services associés.',
    matierePremiere: 'Pétrole Brut (Brent) & Croissance du parc auto',
    impactCorrelation: 'Portée par le développement des infrastructures autoroutières et de l’activité aéroportuaire au Sénégal.',
    produitQuotidien: 'Le carburant à la pompe et les bidons d’huile moteur Quartz.',
    profilLynch: 'Stalwart',
    emoji: '🚗'
  },
  CIEC: {
    symbole: 'CIEC',
    nomCourt: 'CIE CI',
    slogan: 'La lumière et l’énergie dans chaque foyer ivoirien',
    activite: 'Exploite en concession le réseau national de production, transport et distribution d’électricité en Côte d’Ivoire.',
    modeleEco: 'Rémunération garantie par l’État selon un contrat de concession basé sur les kilowattheures distribués et le rendement du réseau.',
    matierePremiere: 'Gaz Naturel & Pluviométrie (Barrages hydro)',
    impactCorrelation: 'Le coût de production dépend du prix du gaz alimentant les turbines thermiques et du niveau d’eau dans les barrages hydroélectriques.',
    produitQuotidien: 'L’électricité à la maison et la facture CIE (ou recharge compteur prépayé).',
    profilLynch: 'Monopole / Rente',
    emoji: '⚡'
  },
  SDCC: {
    symbole: 'SDCC',
    nomCourt: 'SODECI',
    slogan: 'L’eau potable accessible à tous',
    activite: 'Traite, pompe et distribue l’eau potable dans les villes et villages de Côte d’Ivoire.',
    modeleEco: 'Reçoit une rémunération contractuelle régulée sur chaque mètre cube d’eau potable consommé et facturé aux abonnés.',
    matierePremiere: 'Coût de l’Énergie & Produits de traitement de l’eau',
    impactCorrelation: 'Bénéficie de l’urbanisation rapide et de l’augmentation constante du nombre d’abonnés au réseau public d’eau.',
    produitQuotidien: 'L’eau qui coule du robinet et la facture trimestrielle SODECI.',
    profilLynch: 'Monopole / Rente',
    emoji: '🚰'
  },
  SMBC: {
    symbole: 'SMBC',
    nomCourt: 'SMB (Bitumes)',
    slogan: 'Le bitume qui construit les routes de l’Afrique',
    activite: 'Seule raffinerie spécialisée d’Afrique de l’Ouest capable de transformer des pétroles bruts lourds en bitume routier.',
    modeleEco: 'Vend le bitume liquide ou en fûts aux grands groupes de travaux publics (Colas, Razel, Sogea-Satom) pour goudronner les routes.',
    matierePremiere: 'Pétrole Brut Lourd & Budgets d’Infrastructures',
    impactCorrelation: 'Ses marges dépendent du « crack spread » (écart entre le prix du brut lourd acheté et le prix du bitume vendu) et des chantiers routiers régionaux.',
    produitQuotidien: 'Le goudron noir sur lequel roulent nos voitures.',
    profilLynch: 'Monopole / Rente',
    emoji: '🛣️'
  },

  // --- BANQUES & SERVICES FINANCIERS ---
  SGBC: {
    symbole: 'SGBC',
    nomCourt: 'Société Générale CI',
    slogan: 'Le pilier bancaire historique de l’économie régionale',
    activite: 'Première banque de l’UEMOA en total de bilan, accompagnant les multinationales, les PME et les particuliers.',
    modeleEco: 'Gagne la marge d’intérêt (différence entre les taux des prêts accordés et les dépôts rémunérés) + commissions sur les cartes, virements et opérations de change.',
    matierePremiere: 'Taux directeurs BCEAO & Croissance du PIB',
    impactCorrelation: 'Très sensible aux décisions de politique monétaire de la BCEAO et aux grands projets d’investissement du pays.',
    produitQuotidien: 'Les agences SG, les distributeurs de billets et les cartes bancaires.',
    profilLynch: 'Stalwart',
    emoji: '🏦'
  },
  BICC: {
    symbole: 'BICC',
    nomCourt: 'BICICI',
    slogan: 'La banque solide des entreprises et des cadres',
    activite: 'Banque d’affaires et de détail historique en Côte d’Ivoire, désormais détenue par des investisseurs institutionnels ivoiriens de premier plan.',
    modeleEco: 'Prêts aux grandes entreprises ivoiriennes, crédits immobiliers et services de banque privée.',
    matierePremiere: 'Taux BCEAO & Climat des affaires',
    impactCorrelation: 'Profite de la baisse du coût du risque et du dynamisme du crédit aux entreprises.',
    produitQuotidien: 'Les comptes courants professionnels et les crédits d’équipement.',
    profilLynch: 'Stalwart',
    emoji: '🏛️'
  },
  BOAC: {
    symbole: 'BOAC',
    nomCourt: 'BOA Côte d’Ivoire',
    slogan: 'La banque panafricaine proche des entrepreneurs',
    activite: 'Filiale ivoirienne du groupe Bank of Africa, très active dans le financement des PME et des fonctionnaires.',
    modeleEco: 'Crédits aux PME, crédits à la consommation et investissements en obligations souveraines émises par les États de l’UEMOA.',
    matierePremiere: 'Rendement des Titres Publics & Taux BCEAO',
    impactCorrelation: 'Ses profits montent quand le volume de crédits sains augmente et que les rendements des obligations d’État sont attractifs.',
    produitQuotidien: 'Les agences BOA et les crédits aux salariés.',
    profilLynch: 'Rendement Dividende',
    emoji: '💳'
  },
  BOAB: {
    symbole: 'BOAB',
    nomCourt: 'BOA Bénin',
    slogan: 'Le numéro un incontesté du secteur bancaire béninois',
    activite: 'Leader bancaire au Bénin avec le réseau d’agences le plus dense du pays.',
    modeleEco: 'Financement du commerce d’import-export au port de Cotonou et collecte massive d’épargne.',
    matierePremiere: 'Activité portuaire de Cotonou & Taux BCEAO',
    impactCorrelation: 'Étroitement corrélée au commerce régional avec le Nigéria et les pays du Sahel.',
    produitQuotidien: 'Les comptes bancaires et distributeurs BOA au Bénin.',
    profilLynch: 'Rendement Dividende',
    emoji: '🏦'
  },
  BICB: {
    symbole: 'BICB',
    nomCourt: 'BIIC Bénin',
    slogan: 'Née de la fusion de deux banques béninoises',
    activite: 'Banque Internationale pour l’Industrie et le Commerce du Bénin (BIIC), née en 2020 de la fusion de la Banque Africaine de l’Industrie et du Commerce (BAIC) et de la Banque Internationale du Bénin pour l’Économie (BIBE), cotée à la BRVM depuis avril 2025.',
    modeleEco: 'Prêts aux entreprises, crédits immobiliers et particuliers, portée par le commerce portuaire et agricole béninois.',
    matierePremiere: 'Activité portuaire de Cotonou & Taux BCEAO',
    impactCorrelation: 'Étroitement corrélée au dynamisme du commerce régional avec le Nigéria et le Niger, qui transite en grande partie par le port de Cotonou.',
    produitQuotidien: 'Les agences BIIC et les crédits aux commerçants du grand marché de Cotonou.',
    profilLynch: 'Fast Grower',
    emoji: '🏦'
  },
  // Admise à la cote le 24 septembre 2026 (avis n°263-2026/BRVM/DG). Chiffres tirés de
  // sa Note d'Information (visa AMF-UMOA AO/26-03) : PNB 67,8 Mds FCFA et résultat net
  // 27,2 Mds FCFA en 2025, croissance annuelle moyenne de +17,5 % et +15,8 % sur cinq
  // ans — d'où le profil Fast Grower plutôt que Stalwart comme les banques installées.
  BBGC: {
    symbole: 'BBGC',
    nomCourt: 'Bridge Bank CI',
    slogan: 'La banque des entrepreneurs ivoiriens, dernière arrivée à la cote',
    activite: 'Banque universelle fondée en 2006 par le groupe Teyliom, elle finance les grandes entreprises, les PME et les particuliers depuis un réseau de 16 agences en Côte d’Ivoire et une succursale au Sénégal. Cotée à la BRVM depuis le 24 septembre 2026, après la cession de 20 % de son capital au public.',
    modeleEco: 'Gagne la marge d’intérêts (63 % de ses revenus) en prêtant plus cher qu’elle ne rémunère les dépôts, complétée par les commissions bancaires (36 %) sur les moyens de paiement et la gestion de trésorerie des entreprises.',
    matierePremiere: 'Taux BCEAO & Investissement des PME ivoiriennes',
    impactCorrelation: 'Son bénéfice dépend de deux forces opposées : le volume de crédit qu’elle place quand les entreprises investissent, et le coût du risque qu’elle doit provisionner quand elles trébuchent — ce dernier a été multiplié par cinq entre 2021 et 2025.',
    produitQuotidien: 'Les agences Bridge Bank, les crédits aux PME et les cartes bancaires.',
    profilLynch: 'Fast Grower',
    emoji: '🌉'
  },
  BOABF: {
    symbole: 'BOABF',
    nomCourt: 'BOA Burkina Faso',
    slogan: 'Un acteur bancaire résilient au cœur du Faso',
    activite: 'Banque majeure au Burkina Faso pour les entreprises, commerçants et particuliers.',
    modeleEco: 'Prêts commerciaux, financement des campagnes agricoles et détention de bons du Trésor.',
    matierePremiere: 'Taux BCEAO & Conjoncture locale',
    impactCorrelation: 'Soutient des dividendes solides grâce à une gestion rigoureuse de ses coûts et de son portefeuille de titres.',
    produitQuotidien: 'Les crédits de trésorerie et services bancaires.',
    profilLynch: 'Rendement Dividende',
    emoji: '🏦'
  },
  BOAM: {
    symbole: 'BOAM',
    nomCourt: 'BOA Mali',
    slogan: 'La banque de référence au Mali',
    activite: 'Accompagne les acteurs économiques maliens et finance les échanges commerciaux.',
    modeleEco: 'Intermédiation bancaire et services de transfert de fonds.',
    matierePremiere: 'Commerce régional & Taux BCEAO',
    impactCorrelation: 'Capacité à maintenir une rentabilité stable malgré les aléas conjoncturels régionaux.',
    produitQuotidien: 'Comptes d’épargne et crédits aux commerçants maliens.',
    profilLynch: 'Rendement Dividende',
    emoji: '🏦'
  },
  BOAN: {
    symbole: 'BOAN',
    nomCourt: 'BOA Niger',
    slogan: 'Le partenaire bancaire clé du Niger',
    activite: 'Fournit des services bancaires essentiels aux entreprises et ménages nigériens.',
    modeleEco: 'Crédits aux secteurs minier, agricole et commercial nigériens.',
    matierePremiere: 'Taux BCEAO & Stabilité économique',
    impactCorrelation: 'Dépend du niveau d’activité économique et des investissements publics au Niger.',
    produitQuotidien: 'Services de paie et comptes d’épargne au Niger.',
    profilLynch: 'Rendement Dividende',
    emoji: '🏦'
  },
  BOAS: {
    symbole: 'BOAS',
    nomCourt: 'BOA Sénégal',
    slogan: 'La banque en pleine expansion au pays de la Teranga',
    activite: 'Filiale sénégalaise du groupe BOA, en forte croissance sur le marché dakarois.',
    modeleEco: 'Crédits aux PME sénégalaises, financements de projets immobiliers et collecte de dépôts.',
    matierePremiere: 'Croissance du PIB sénégalais & Taux BCEAO',
    impactCorrelation: 'Bénéficie de l’essor économique du Sénégal et des nouveaux projets énergétiques.',
    produitQuotidien: 'Comptes et cartes bancaires à Dakar et en région.',
    profilLynch: 'Fast Grower',
    emoji: '🏦'
  },
  CBIBF: {
    symbole: 'CBIBF',
    nomCourt: 'Coris Bank Int.',
    slogan: 'La banque autrement, championne des PME et des entrepreneurs',
    activite: 'Né au Burkina Faso sous l’impulsion d’Idrissa Nassa, Coris Bank est devenu un groupe bancaire panafricain majeur réputé pour sa rapidité de décision et son ancrage auprès du secteur privé.',
    modeleEco: 'Financement agile des PME/PMI, crédits aux importateurs, financement de chantiers et de campagnes agricoles, complétés par la banque de détail et des solutions spécialisées (leasing, affacturage, fenêtre islamique Baraka).',
    matierePremiere: 'Dynamisme des PME, cours de l’or / coton & Taux BCEAO',
    impactCorrelation: 'Profite de gains de parts de marché constants grâce à sa flexibilité opérationnelle et à son expansion réussie dans toute la sous-région.',
    produitQuotidien: 'Les crédits de trésorerie express pour commerçants, les agences Coris et les comptes d’entreprise.',
    profilLynch: 'Fast Grower',
    emoji: '🚀'
  },
  SIBC: {
    symbole: 'SIBC',
    nomCourt: 'SIB CI (Attijari)',
    slogan: 'L’efficacité bancaire adossée à un groupe international',
    activite: 'Filiale ivoirienne du groupe Attijariwafa bank, positionnée sur les entreprises et les fonctionnaires.',
    modeleEco: 'Financement des grands travaux de BTP, crédits à la consommation et bancassurance.',
    matierePremiere: 'Taux BCEAO & Chantiers d’infrastructures',
    impactCorrelation: 'Génère un retour sur fonds propres (ROE) parmi les plus élevés de la place bancaire ivoirienne.',
    produitQuotidien: 'Les cartes SIB et les crédits auto/habitat.',
    profilLynch: 'Stalwart',
    emoji: '🏦'
  },
  NSBC: {
    symbole: 'NSBC',
    nomCourt: 'NSIA Banque CI',
    slogan: 'L’alliance de la banque et de l’assurance',
    activite: 'Pôle bancaire du groupe NSIA, combinant services bancaires et contrats d’assurance.',
    modeleEco: 'Propose à ses clients des forfaits complets (compte bancaire + assurance vie/santé) pour fidéliser les dépôts.',
    matierePremiere: 'Taux de bancarisation & Taux BCEAO',
    impactCorrelation: 'Profite de synergies uniques entre les réseaux d’agences bancaires et les courtiers d’assurance.',
    produitQuotidien: 'Les comptes bancaires adossés à une prévoyance retraite.',
    profilLynch: 'Stalwart',
    emoji: '🛡️'
  },
  ECOC: {
    symbole: 'ECOC',
    nomCourt: 'Ecobank CI',
    slogan: 'La banque panafricaine connectée et digitale',
    activite: 'Filiale ivoirienne du géant panafricain Ecobank, pionnière de la banque mobile.',
    modeleEco: 'Transactions transfrontalières, services aux multinationales et paiements par QR code / appli mobile.',
    matierePremiere: 'Volumes des échanges régionaux & Taux BCEAO',
    impactCorrelation: 'Bénéficie de son réseau international pour capter les flux financiers entre pays d’Afrique de l’Ouest.',
    produitQuotidien: 'L’application mobile Ecobank et les paiements marchands.',
    profilLynch: 'Stalwart',
    emoji: '🌐'
  },
  ETIT: {
    symbole: 'ETIT',
    nomCourt: 'Ecobank Transnational',
    slogan: 'Le géant bancaire panafricain présent dans 35 pays',
    activite: 'Maison-mère basée à Lomé, chapeautant le réseau bancaire le plus étendu de toute l’Afrique subsaharienne.',
    modeleEco: 'Perçoit les dividendes de ses filiales bancaires et réalise des opérations de trésorerie sur les devises africaines.',
    matierePremiere: 'Taux de change devises (Naira, Cedi, FCFA) & PIB africain',
    impactCorrelation: 'Très sensible aux fluctuations des monnaies africaines (notamment le Naira nigérian) et au commerce continental.',
    produitQuotidien: 'Le réseau de distributeurs et d’agences partout en Afrique.',
    profilLynch: 'Turnaround',
    emoji: '🌍'
  },

  // --- INDUSTRIE & MATÉRIAUX ---
  SLBC: {
    symbole: 'SLBC',
    nomCourt: 'Solibra',
    slogan: 'Le maître des bières, boissons rafraîchissantes et eaux minérales',
    activite: 'Brasseur et embouteilleur leader en Côte d’Ivoire (groupe Castel / BGI), fabricant de bières phares (Bock « 66 », Beaufort, Castel, Doppel, Corona, Budweiser), de boissons gazeuses propres (World Cola, Youki, Youzou, Orangina) et d’eaux minérales (Awa, Cristaline).',
    modeleEco: 'Vend des millions de bouteilles chaque jour aux dépôts de boissons, maquis, restaurants et supermarchés.',
    matierePremiere: 'Sucre, Malt d’orge, Verre & Pouvoir d’achat festif',
    impactCorrelation: 'Ses coûts dépendent du prix mondial du sucre, du malt d’orge et de l’énergie pour les usines d’embouteillage.',
    produitQuotidien: 'La bouteille de bière Bock « 66 », la bouteille de World Cola et l’eau minérale Awa.',
    profilLynch: 'Stalwart',
    emoji: '🍻'
  },
  NTLC: {
    symbole: 'NTLC',
    nomCourt: 'Nestlé CI',
    slogan: 'Le café, le lait et les bouillons de la maison',
    activite: 'Filiale ivoirienne du géant suisse Nestlé, elle fabrique des cafés solubles, du lait en poudre, des céréales infantiles et des bouillons culinaires dans ses usines locales.',
    modeleEco: 'Vend ses marques mondiales (Nescafé, Milo, Maggi) aux supermarchés, épiceries de quartier et grossistes de toute la sous-région.',
    matierePremiere: 'Cours du Café, du Cacao & du Lait en poudre',
    impactCorrelation: 'Ses marges dépendent des cours mondiaux de ses matières premières agricoles et du pouvoir d’achat des ménages qui arbitrent entre marques.',
    produitQuotidien: 'Le Nescafé du matin, le Milo des enfants et le cube Maggi dans la sauce.',
    profilLynch: 'Stalwart',
    emoji: '☕'
  },
  UNLC: {
    symbole: 'UNLC',
    nomCourt: 'Unilever CI',
    slogan: 'Le savon, la lessive et le dentifrice de tous les foyers',
    activite: 'Filiale ivoirienne du groupe anglo-néerlandais Unilever, fabrique des savons de ménage, des lessives, des dentifrices et des produits d’hygiène corporelle.',
    modeleEco: 'Vend ses marques mondiales (OMO, Lux, Signal, Sunlight) aux grossistes et supermarchés de Côte d’Ivoire et des pays voisins.',
    matierePremiere: 'Huile de Palme, Pétrochimie (tensioactifs) & Pouvoir d’achat',
    impactCorrelation: 'Ses coûts de production suivent les cours de l’huile de palme et des dérivés pétrochimiques utilisés dans les savons et lessives.',
    produitQuotidien: 'La lessive OMO, le savon Lux et le dentifrice Signal dans la salle de bain.',
    profilLynch: 'Stalwart',
    emoji: '🧼'
  },
  CABC: {
    symbole: 'CABC',
    nomCourt: 'Sicable CI',
    slogan: 'Le fil conducteur de l’énergie électrique',
    activite: 'Fabrique des câbles électriques industriels et domestiques en cuivre et en aluminium (filiale à 51% du groupe mondial Prysmian).',
    modeleEco: 'Fournit les installateurs électriques, les chantiers de construction et les compagnies d’électricité comme la CIE.',
    matierePremiere: 'Cuivre & Aluminium (LME Londres)',
    impactCorrelation: 'Ajuste ses prix de vente selon le cours mondial du cuivre et de l’aluminium à la bourse de Londres.',
    produitQuotidien: 'Les câbles électriques encastrés dans les murs pour allumer les lampes.',
    profilLynch: 'Cyclique',
    emoji: '🔌'
  },
  SICC: {
    symbole: 'SICC',
    nomCourt: 'SICOR CI (Coco Râpé)',
    slogan: 'La noix de coco transformée, unique en Afrique de l’Ouest',
    activite: 'Seul acteur industriel de la transformation de la noix de coco en Afrique de l’Ouest : SICOR cultive et transforme la coco dans la région d’Assinie-Jacqueville en coco râpé séché, huile de coprah et tourteaux.',
    modeleEco: 'Exporte l’essentiel de sa production (coco râpé, huile de coprah) vers l’industrie agroalimentaire internationale (biscuiterie, confiserie).',
    matierePremiere: 'Cours mondial du Coprah/Coco & Récoltes de Noix de Coco',
    impactCorrelation: 'Son résultat dépend des volumes récoltés dans ses plantations et des prix mondiaux des dérivés de coco, sur un titre à faible flottant et forte volatilité.',
    produitQuotidien: 'La noix de coco râpée séchée utilisée dans les biscuits et confiseries.',
    profilLynch: 'Cyclique',
    emoji: '🌰'
  },
  FTSC: {
    symbole: 'FTSC',
    nomCourt: 'Filtisac CI',
    slogan: 'L’emballage textile qui protège les récoltes de cacao',
    activite: 'Filiale du groupe IPS West Africa (fonds AKFED), Filtisac fabrique des sacs en fibre de jute et en polypropylène tissé, ainsi que des ficelles et cordages industriels.',
    modeleEco: 'Vend des millions de sacs aux coopératives et négociants qui exportent le cacao, le café, le cajou et les engrais.',
    matierePremiere: 'Fibre de Jute brute, Polymères & Récolte de Cacao',
    impactCorrelation: 'Plus la campagne agricole de cacao et d’anacarde est abondante, plus la demande de sacs d’emballage explose.',
    produitQuotidien: 'Les grands sacs en toile marron empilés au port pour le cacao.',
    profilLynch: 'Cyclique',
    emoji: '🎒'
  },
  SIVC: {
    symbole: 'SIVC',
    nomCourt: 'Erium CI (ex-Air Liquide)',
    slogan: 'Les gaz essentiels à la santé et à l’industrie',
    activite: 'Ancienne filiale d’Air Liquide reprise par le fonds Adenia Partners en 2024 sous la marque Erium, elle produit et distribue des gaz médicaux (oxygène pour hôpitaux) et industriels (acétylène, argon pour soudure).',
    modeleEco: 'Livraison régulière de bouteilles sous pression et installation de réseaux fluides chez les hôpitaux, cliniques et industriels.',
    matierePremiere: 'Coût de l’Électricité (usines de séparation d’air)',
    impactCorrelation: 'L’électricité est son premier coût de fabrication ; la demande hospitalière et les chantiers industriels assurent une base de revenus solide.',
    produitQuotidien: 'Les bouteilles d’oxygène médical qui alimentent les hôpitaux.',
    profilLynch: 'Stalwart',
    emoji: '🧪'
  },
  UNXC: {
    symbole: 'UNXC',
    nomCourt: 'Uniwax CI',
    slogan: 'L’art textile et l’élégance du véritable Wax ouest-africain',
    activite: 'Seul fabricant industriel en Afrique de l’Ouest de véritables pagnes imprimés Wax de haute qualité (usine à Yopougon, Abidjan), passé sous le contrôle de la Compagnie Ivoirienne de Coton (COIC) début 2026 pour créer une filière textile 100% ivoirienne intégrée du champ de coton à l’étoffe.',
    modeleEco: 'Conçoit et imprime des motifs exclusifs vendus en coupons de 6 yards aux grossistes, couturiers et créateurs de mode.',
    matierePremiere: 'Coton brut ivoirien, Tissus écrus & Colorants',
    impactCorrelation: 'Sensible au prix mondial du coton, à la lutte contre les contrefaçons textiles importées d’Asie et aux synergies d’approvisionnement direct avec la COIC.',
    produitQuotidien: 'Le pagne Wax porté lors des grandes fêtes, mariages et cérémonies.',
    profilLynch: 'Turnaround',
    emoji: '👗'
  },
  SEMC: {
    symbole: 'SEMC',
    nomCourt: 'Sonoco Packaging SIEM',
    slogan: 'L’emballage métallique qui protège et conserve nos aliments',
    activite: 'Leader historique des emballages métalliques en Côte d’Ivoire (usine à Vridi, Abidjan), SIEM est intégrée au groupe américain Sonoco (Sonoco Metal Packaging) suite au rachat mondial d’Eviosys.',
    modeleEco: 'Fabrique des boîtes de conserve en fer-blanc pour les conserveries de thon d’Abidjan (Scodi, Pêche et Froid), le concentré de tomate et les bidons de peinture.',
    matierePremiere: 'Fer blanc (Acier) & Campagnes de pêche au thon',
    impactCorrelation: 'Ses marges dépendent du cours mondial de l’acier et des tonnages de thon débarqués et mis en boîte au port d’Abidjan.',
    produitQuotidien: 'La boîte de thon en conserve et la boîte de concentré de tomate.',
    profilLynch: 'Cyclique',
    emoji: '🥫'
  },
  STBC: {
    symbole: 'STBC',
    nomCourt: 'SITAB (Tabac)',
    slogan: 'La distribution structurée de produits sous licence',
    activite: 'Filiale du groupe international Imperial Brands, fabrique et distribue des produits du tabac sous licence de marques reconnues.',
    modeleEco: 'Vente aux distributeurs agréés avec des flux de trésorerie prévisibles et d’excellentes marges.',
    matierePremiere: 'Taxes d’accises fiscales & Tabac brut',
    impactCorrelation: 'Très dépendante des augmentations de taxes gouvernementales sur le tabac.',
    produitQuotidien: 'Les paquets de cigarettes chez les revendeurs agréés.',
    profilLynch: 'Rendement Dividende',
    emoji: '📦'
  },

  // --- LOGISTIQUE & SERVICES ---
  SDSC: {
    symbole: 'SDSC',
    nomCourt: 'Africa Global Logistics CI',
    slogan: 'La porte d’entrée logistique et portuaire de la région',
    activite: 'Filiale du groupe MSC (premier armateur mondial), AGL CI gère les terminaux à conteneurs d’Abidjan et San Pedro, la manutention portuaire, le transit douanier et la logistique vers les pays de l’hinterland (Mali, Burkina Faso).',
    modeleEco: 'Facture les compagnies maritimes et importateurs pour chaque conteneur manutentionné (TEU), stocké et dédouané.',
    matierePremiere: 'Trafic maritime mondial & Volume du cacao exporté',
    impactCorrelation: 'Suit fidèlement le volume du commerce extérieur de la Côte d’Ivoire et les flux de transit sous-régionaux.',
    produitQuotidien: 'Les grands conteneurs maritimes et les portiques géants du port d’Abidjan.',
    profilLynch: 'Stalwart',
    emoji: '🚢'
  },
  ABJC: {
    symbole: 'ABJC',
    nomCourt: 'Servair Abidjan',
    slogan: 'La gastronomie et le service au décollage des avions',
    activite: 'Filiale du Groupe Servair (détenu par Air France-KLM et Gategroup), elle prépare les repas servis à bord des vols internationaux à l’aéroport d’Abidjan et gère les salons VIP et bars d’aéroport.',
    modeleEco: 'Facture les compagnies aériennes (Air France, Corsair, etc.) pour chaque plateau repas chargé dans les avions.',
    matierePremiere: 'Trafic Aérien International & Tourisme d’affaires',
    impactCorrelation: 'Directement liée au nombre de vols et de passagers transitant par l’aéroport international Félix Houphouët-Boigny.',
    produitQuotidien: 'Le plateau repas et la boisson servis en plein vol dans l’avion.',
    profilLynch: 'Cyclique',
    emoji: '✈️'
  },
  CFAC: {
    symbole: 'CFAC',
    nomCourt: 'CFAO Motors CI',
    slogan: 'La mobilité automobile et le service après-vente',
    activite: 'Filiale du groupe CFAO (détenu par Toyota Tsusho Corporation), leader de la distribution automobile en Côte d’Ivoire (Toyota, Suzuki, Mitsubishi, Peugeot, Citroën, motos Yamaha).',
    modeleEco: 'Marge sur la vente de voitures neuves + forte rentabilité récurrente sur les pièces détachées d’origine et l’entretien d’atelier.',
    matierePremiere: 'Parité Yen/Euro, Pouvoir d’achat & Crédit auto',
    impactCorrelation: 'Dépend du pouvoir d’achat de la classe moyenne, du renouvellement des flottes d’entreprises et des taux de crédit bancaire.',
    produitQuotidien: 'Les taxis compteurs Suzuki et les 4x4 Toyota que l’on croise tous les jours en ville.',
    profilLynch: 'Cyclique',
    emoji: '🚘'
  },
  PRSC: {
    symbole: 'PRSC',
    nomCourt: 'Tractafric Motors CI',
    slogan: 'La distribution automobile multimarque et la mobilité',
    activite: 'Filiale du groupe Optorg (holding Al Mada), Tractafric Motors CI distribue des marques automobiles de premier plan (BMW, MINI, Hyundai, Ford, Mazda), assure la location de véhicules sous franchise Europcar et distribue les pneus Michelin.',
    modeleEco: 'Marge commerciale sur les ventes de véhicules neufs particuliers et utilitaires, complétée par les pièces d’origine, l’atelier après-vente et la location Europcar.',
    matierePremiere: 'Pouvoir d’achat, Taux de crédit auto & Parité devises',
    impactCorrelation: 'Bénéficie de l’essor des flottes d’entreprises, de la demande des classes moyennes et du dynamisme du réseau routier.',
    produitQuotidien: 'Les véhicules Hyundai ou Ford en ville et les voitures de location Europcar.',
    profilLynch: 'Cyclique',
    emoji: '🚗'
  },
  BNBC: {
    symbole: 'BNBC',
    nomCourt: 'Bernabé CI',
    slogan: 'Le grand magasin de l’outillage et du bricolage',
    activite: 'Filiale du groupe Yeshi Group, chaîne de magasins spécialisés en quincaillerie industrielle, outillage professionnel, matériaux de construction, plomberie et sanitaire (Bernabé / TechnoBat).',
    modeleEco: 'Marge commerciale sur la vente d’outils aux artisans, bricoleurs et entreprises de construction.',
    matierePremiere: 'Prix mondial de l’Acier & Dynamique du BTP',
    impactCorrelation: 'Suit le rythme des rénovations immobilières et des constructions urbaines.',
    produitQuotidien: 'Les perceuses, serrures, vis et marteaux dans la boîte à outils.',
    profilLynch: 'Cyclique',
    emoji: '🔧'
  },
  NEIC: {
    symbole: 'NEIC',
    nomCourt: 'NEI-CEDA',
    slogan: 'Les livres scolaires qui instruisent la jeunesse',
    activite: 'Filiale du groupe Hachette Livre (70,6%), NEI-CEDA est l’éditeur et distributeur leader de manuels scolaires officiels agréés et de littérature générale en Côte d’Ivoire.',
    modeleEco: 'Vente massive de livres de classe aux familles et librairies lors de la grande rentrée scolaire de septembre.',
    matierePremiere: 'Prix de la pâte à papier & Budget éducation',
    impactCorrelation: 'Activité très saisonnière centrée sur le 3ème trimestre (rentrée des classes) et les programmes du Ministère de l’Éducation.',
    produitQuotidien: 'Les livres de français et de mathématiques dans le cartable des élèves.',
    profilLynch: 'Stalwart',
    emoji: '📚'
  },
  STAC: {
    symbole: 'STAC',
    nomCourt: 'SETAO (Bouygues)',
    slogan: 'Les grands chantiers de génie civil et de bâtiment',
    activite: 'Filiale du groupe Bouygues (détenue à 78,6% par Bouygues Bâtiment International), SETAO est un bâtisseur historique de référence en Côte d’Ivoire, réalisant des tours de bureaux, usines, hôpitaux et ouvrages d’art.',
    modeleEco: 'Facturation contractuelle à l’avancement des travaux sur les grands projets d’infrastructure publics et privés.',
    matierePremiere: 'Coût du Ciment, Acier & Commandes d’Infrastructures',
    impactCorrelation: 'Directement liée aux grands appels d’offres de construction et aux budgets d’infrastructures de l’État et des promoteurs.',
    produitQuotidien: 'Les grands immeubles modernes, usines et ponts en chantier.',
    profilLynch: 'Cyclique',
    emoji: '🏗️'
  },
  SAFC: {
    symbole: 'SAFC',
    nomCourt: 'SAFCA CI (Alios Finance)',
    slogan: 'Le pionnier du crédit-bail pour équiper les entreprises',
    activite: 'Établissement financier historique spécialisé dans le crédit-bail (leasing) mobilier et d’équipement pour PME et professionnels, repris en 2025 par le groupe ivoirien CREDAF GROUP (Crédit d’Afrique).',
    modeleEco: 'Finance l’achat de véhicules utilitaires, engins et matériels industriels qu’elle loue aux entreprises avec option de rachat finale.',
    matierePremiere: 'Taux d’intérêt BCEAO & Investissements PME',
    impactCorrelation: 'Profite de la dynamique de rééquipement des PME et de sa restructuration réussie sous sa nouvelle gouvernance.',
    produitQuotidien: 'Le camion de livraison loué par une petite entreprise pour démarrer son activité.',
    profilLynch: 'Stalwart',
    emoji: '📑'
  },

  // --- LOISIRS & SERVICES PUBLICS SPÉCIALISÉS ---
  LNBB: {
    symbole: 'LNBB',
    nomCourt: 'Loterie Nationale du Bénin',
    slogan: 'Le monopole d’État des jeux de hasard depuis 1967',
    activite: 'Détient le monopole légal des jeux de hasard, paris et loteries (tickets à gratter, tirages) au Bénin depuis 1967 ; les bénéfices financent aussi des projets sociaux, culturels et sportifs.',
    modeleEco: 'Encaisse la mise des joueurs sur les tickets à gratter et les paris, reverse les gains et garde la marge ; cotée à la BRVM depuis décembre 2024.',
    matierePremiere: 'Pouvoir d’achat des ménages & Concurrence des paris illégaux',
    impactCorrelation: 'Le monopole légal est challengé par des opérateurs de paris privés et illégaux : le résultat net a chuté de 88 % au 1er semestre 2025 sous cette pression concurrentielle.',
    produitQuotidien: 'Le ticket à gratter acheté au kiosque du quartier.',
    profilLynch: 'Monopole / Rente',
    emoji: '🎟️'
  }
};

/**
 * Récupère l'histoire d'une société à partir de son symbole.
 * Si le symbole n'est pas trouvé, génère une fiche générique intelligente basée sur le secteur.
 */
export function getCompanyStory(symbole?: string, nomSociete?: string, secteur?: string): CompanyStory {
  const cleanSymbol = (symbole || '').trim().toUpperCase();
  
  if (cleanSymbol && COMPANY_STORIES[cleanSymbol]) {
    return COMPANY_STORIES[cleanSymbol];
  }

  // Fallback élégant si valeur non répertoriée
  return {
    symbole: cleanSymbol || 'BRVM',
    nomCourt: nomSociete || cleanSymbol,
    slogan: `Acteur clé du secteur ${secteur || 'économique'} en Afrique de l’Ouest`,
    activite: `${nomSociete || 'Cette entreprise'} opère dans le secteur ${secteur || 'régional'} et propose des biens ou services aux particuliers et aux entreprises de la zone UEMOA.`,
    modeleEco: 'Génère du chiffre d’affaires en vendant ses produits ou services à ses clients, avec pour objectif de dégager des bénéfices et de verser des dividendes à ses actionnaires.',
    matierePremiere: 'Activité économique régionale & Taux BCEAO',
    impactCorrelation: 'Son cours de bourse reflète sa capacité à faire croître ses revenus et à maîtriser ses coûts dans son secteur d’activité.',
    produitQuotidien: `Les produits et services du secteur ${secteur || 'de l’entreprise'}.`,
    profilLynch: 'Stalwart',
    emoji: '🏢'
  };
}
